import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Users, User, Plus, Edit, ExternalLink, Trash2, Search, Phone, Mail,
  MapPin, Cake, AlertTriangle, Briefcase,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── canonical helpers ─────────────────────────────────────────────────────────
// Display-only mirror of backend utils/owner_compat.owners_from_flats: lets a
// legacy contact (no owners[]) render its owner info. The moment the operator
// edits anything here we send an EXPLICIT owners[] and the backend becomes
// canonical + regenerates the legacy mirrors. Never write owner_* flats from
// React — owner_compat on the backend is the single mirror authority.
export const ownersFromFlats = (c = {}) => {
  const links = [];
  const s = (v) => (typeof v === 'string' ? v.trim() || null : v ?? null);
  if ([c.owner_name, c.owner_dob, c.owner_address, c.owner_city, c.owner_state, c.owner_zip].some(s)) {
    links.push({
      order: 0, person_id: null, is_owner: true, is_guarantor: true,
      ownership_pct: null, title: null,
      name: s(c.owner_name), cell_phone: null, email: null,
      dob: s(c.owner_dob?.split?.('T')[0] ?? c.owner_dob),
      home_address: { street1: s(c.owner_address), street2: null, city: s(c.owner_city), state: s(c.owner_state), zip: s(c.owner_zip) },
      _legacy: true,
    });
  }
  if (c.has_second_owner || [c.secondary_owner_name, c.secondary_owner_email, c.secondary_owner_phone,
      c.secondary_owner_address, c.secondary_owner_dob, c.secondary_owner_title].some(s)) {
    links.push({
      order: 1, person_id: null, is_owner: true, is_guarantor: true,
      ownership_pct: c.secondary_owner_ownership_pct ?? null, title: s(c.secondary_owner_title),
      name: s(c.secondary_owner_name), cell_phone: s(c.secondary_owner_phone), email: s(c.secondary_owner_email),
      dob: s(c.secondary_owner_dob?.split?.('T')[0] ?? c.secondary_owner_dob),
      home_address: { street1: s(c.secondary_owner_address), street2: null, city: null, state: null, zip: null },
      _legacy: true,
    });
  }
  return links;
};

const resolveIdentity = (link, person) => {
  if (link.person_id && person) {
    return {
      name: person.company_name || person.name,
      cell_phone: person.phone, email: person.email, dob: person.dob,
      home_address: person.home_address || {},
    };
  }
  return {
    name: link.name, cell_phone: link.cell_phone, email: link.email, dob: link.dob,
    home_address: link.home_address || {},
  };
};

const fmtAddress = (ha = {}) => {
  const line1 = [ha.street1, ha.street2].filter(Boolean).join(', ');
  const line2 = [ha.city, ha.state].filter(Boolean).join(', ') + (ha.zip ? ` ${ha.zip}` : '');
  return [line1, line2.trim()].filter(Boolean);
};

const genShortcode = (name = '') => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const base = words.length <= 1 ? (words[0] || '').slice(0, 4) : words.map((w) => w[0]).join('');
  return base.toUpperCase();
};

const emptyIdentity = { name: '', cell_phone: '', email: '', dob: '', home_address: { street1: '', street2: '', city: '', state: '', zip: '' } };
const emptyRelationship = { title: '', ownership_pct: '', is_owner: true, is_guarantor: true };

// ── businesses a person owns/guarantees (rendered on Person profiles) ─────────
export const PersonBusinesses = ({ personId, getAuthHeaders }) => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState(null);
  useEffect(() => {
    let live = true;
    axios.get(`${API}/contacts`, { headers: getAuthHeaders() })
      .then((res) => {
        if (!live) return;
        setBusinesses(res.data.filter((c) => (c.owners || []).some((l) => l.person_id === personId)));
      })
      .catch(() => live && setBusinesses([]));
    return () => { live = false; };
  }, [personId, getAuthHeaders]);

  return (
    <Card data-testid="person-businesses-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="h-5 w-5" />
          Businesses
        </CardTitle>
        <CardDescription>Companies this person owns or guarantees</CardDescription>
      </CardHeader>
      <CardContent>
        {businesses === null ? (
          <div className="text-center py-4"><div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" /></div>
        ) : businesses.length === 0 ? (
          <p className="text-sm text-gray-400 italic">Not linked to any business yet. Link this person from a business profile's Owners &amp; Guarantors section.</p>
        ) : (
          <div className="space-y-2">
            {businesses.map((b) => {
              const link = (b.owners || []).find((l) => l.person_id === personId) || {};
              return (
                <button
                  key={b.id}
                  onClick={() => navigate(`/contacts/${b.id}`)}
                  className="w-full flex items-center justify-between gap-3 border rounded-lg px-4 py-3 hover:bg-blue-50 text-left"
                  data-testid={`person-business-${b.shortcode}`}
                >
                  <div>
                    <p className="font-medium">{b.company_name} <span className="font-mono text-xs text-blue-600">({b.shortcode})</span></p>
                    <p className="text-xs text-gray-500">{link.title || 'No title'}{link.ownership_pct != null ? ` · ${link.ownership_pct}% ownership` : ''}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {link.is_owner && <Badge variant="outline" className="text-xs">Owner</Badge>}
                    {link.is_guarantor && <Badge className="bg-amber-100 text-amber-800 text-xs">Guarantor</Badge>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── main section ──────────────────────────────────────────────────────────────
const OwnersSection = ({ contact, isAdmin, getAuthHeaders, onChanged }) => {
  const navigate = useNavigate();
  const [persons, setPersons] = useState({});
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editRel, setEditRel] = useState(null);       // {index, ...relationship}
  const [editIdent, setEditIdent] = useState(null);   // {index, ...identity}
  const [removeIdx, setRemoveIdx] = useState(null);

  const canonical = Array.isArray(contact.owners) && contact.owners.length > 0;
  const legacyOwners = useMemo(() => ownersFromFlats(contact), [contact]);
  const owners = useMemo(
    () => (canonical ? [...contact.owners].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) : []),
    [contact, canonical]
  );

  // resolve referenced person records
  useEffect(() => {
    const ids = [...new Set(owners.map((l) => l.person_id).filter(Boolean))].filter((pid) => !persons[pid]);
    if (!ids.length) return;
    Promise.all(ids.map((pid) =>
      axios.get(`${API}/contacts/${pid}`, { headers: getAuthHeaders() })
        .then((r) => [pid, r.data]).catch(() => [pid, null])
    )).then((pairs) => setPersons((prev) => ({ ...prev, ...Object.fromEntries(pairs) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owners]);

  // The ONE save path: send the whole contact with an EXPLICIT owners[] —
  // presence-aware backend semantics make this authoritative and regenerate
  // the legacy mirror fields via owner_compat.
  const saveOwners = useCallback(async (nextOwners, successMsg) => {
    setSaving(true);
    try {
      const body = { ...contact };
      ['id', '_id', 'created_at', 'updated_at', 'contact_types'].forEach((k) => delete body[k]);
      body.owners = nextOwners.map(({ _legacy, ...link }, i) => ({ ...link, order: link.order ?? i }));
      await axios.put(`${API}/contacts/${contact.id}`, body, { headers: getAuthHeaders() });
      toast.success(successMsg);
      onChanged && onChanged();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to save owners');
    } finally {
      setSaving(false);
    }
  }, [contact, getAuthHeaders, onChanged]);

  const handleRemove = async () => {
    const next = owners.filter((_, i) => i !== removeIdx).map((l, i) => ({ ...l, order: i }));
    setRemoveIdx(null);
    await saveOwners(next, 'Owner removed');
  };

  return (
    <Card className="mt-4" data-testid="owners-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Owners &amp; Guarantors
          </CardTitle>
          {isAdmin && (
            <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-owner-btn" disabled={saving}>
              <Plus className="h-4 w-4 mr-1" /> Add Owner
            </Button>
          )}
        </div>
        <CardDescription>
          People who own or guarantee this business. Identity comes from the person's Address Book record; ownership &amp; title are specific to this business.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {owners.length === 0 ? (
          <div className="text-center py-8 text-gray-400" data-testid="owners-empty-state">
            <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No owners or guarantors recorded yet.</p>
            {legacyOwners.length > 0 && (
              <div className="mt-3 mx-auto max-w-lg rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800" data-testid="legacy-owner-notice">
                <p className="text-sm font-medium">Legacy primary contact: {legacyOwners.map((owner) => owner.name).filter(Boolean).join(', ') || 'Name not provided'}</p>
                <p className="text-xs mt-1">This is legacy contact information, not a canonical owner or guarantor link. No records were migrated.</p>
              </div>
            )}
            {isAdmin && <p className="text-xs mt-1">Use "Add Owner" to link a person from the Address Book.</p>}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {owners.map((link, idx) => {
              const person = link.person_id ? persons[link.person_id] : null;
              const ident = resolveIdentity(link, person);
              const addr = fmtAddress(ident.home_address);
              return (
                <div key={link.owner_id || idx} className="border rounded-lg p-4 space-y-2" data-testid={`owner-card-${idx}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${link.person_id ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>
                        <User className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate" data-testid={`owner-name-${idx}`}>
                          {ident.name || <span className="text-gray-400 italic">Unnamed owner</span>}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {link.title || 'No title'}
                          {link.ownership_pct != null && link.ownership_pct !== '' ? ` · ${link.ownership_pct}% ownership` : ''}
                          {idx === 0 ? ' · primary' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {link.is_owner !== false && <Badge variant="outline" className="text-xs">Owner</Badge>}
                      {link.is_guarantor !== false && <Badge className="bg-amber-100 text-amber-800 text-xs">Guarantor</Badge>}
                    </div>
                  </div>

                  <div className="text-sm text-gray-600 space-y-1">
                    {ident.cell_phone && <p className="flex items-center gap-2"><Phone className="h-3 w-3 text-gray-400" />{ident.cell_phone}</p>}
                    {ident.email && <p className="flex items-center gap-2"><Mail className="h-3 w-3 text-gray-400" />{ident.email}</p>}
                    {ident.dob && <p className="flex items-center gap-2"><Cake className="h-3 w-3 text-gray-400" />{ident.dob?.split?.('T')[0] || ident.dob}</p>}
                    {addr.length > 0 && (
                      <p className="flex items-start gap-2"><MapPin className="h-3 w-3 text-gray-400 mt-1 shrink-0" /><span>{addr.map((l, i) => <span key={i} className="block">{l}</span>)}</span></p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 pt-1 border-t">
                    {link.person_id ? (
                      <Button variant="ghost" size="sm" className="h-7 text-blue-700" onClick={() => navigate(`/contacts/${link.person_id}`)} data-testid={`owner-open-person-${idx}`}>
                        <ExternalLink className="h-3 w-3 mr-1" /> Person record
                      </Button>
                    ) : (isAdmin && (
                      <Button variant="ghost" size="sm" className="h-7" onClick={() => setEditIdent({ index: idx, ...emptyIdentity, ...link, home_address: { ...emptyIdentity.home_address, ...(link.home_address || {}) } })} data-testid={`owner-edit-identity-${idx}`}>
                        <Edit className="h-3 w-3 mr-1" /> Identity
                      </Button>
                    ))}
                    {isAdmin && (
                      <Button variant="ghost" size="sm" className="h-7" onClick={() => setEditRel({ index: idx, title: link.title || '', ownership_pct: link.ownership_pct ?? '', is_owner: link.is_owner !== false, is_guarantor: link.is_guarantor !== false })} data-testid={`owner-edit-relationship-${idx}`}>
                        <Edit className="h-3 w-3 mr-1" /> Relationship
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="ghost" size="sm" className="h-7 text-red-600 hover:text-red-700 ml-auto" onClick={() => setRemoveIdx(idx)} data-testid={`owner-remove-${idx}`}>
                        <Trash2 className="h-3 w-3 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Add Owner */}
      <AddOwnerDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        getAuthHeaders={getAuthHeaders}
        businessName={contact.company_name}
        excludePersonIds={owners.map((l) => l.person_id).filter(Boolean)}
        onAdd={async (link) => {
          setAddOpen(false);
          await saveOwners([...owners, { ...link, order: owners.length }], 'Owner added');
        }}
      />

      {/* Edit relationship */}
      <Dialog open={!!editRel} onOpenChange={(v) => !v && setEditRel(null)}>
        <DialogContent className="max-w-md" data-testid="owner-relationship-dialog">
          <DialogHeader><DialogTitle>Relationship to {contact.company_name}</DialogTitle></DialogHeader>
          {editRel && (
            <div className="space-y-4">
              <RelationshipFields value={editRel} onChange={setEditRel} />
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditRel(null)}>Cancel</Button>
                <Button
                  disabled={saving}
                  onClick={async () => {
                    const { index, ...rel } = editRel;
                    const next = owners.map((l, i) => (i === index ? {
                      ...l,
                      title: rel.title || null,
                      ownership_pct: rel.ownership_pct === '' || rel.ownership_pct === null ? null : parseFloat(rel.ownership_pct),
                      is_owner: !!rel.is_owner, is_guarantor: !!rel.is_guarantor,
                    } : l));
                    setEditRel(null);
                    await saveOwners(next, 'Relationship updated');
                  }}
                  data-testid="owner-relationship-save"
                >
                  Save
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit inline identity (only while not linked to a person record) */}
      <Dialog open={!!editIdent} onOpenChange={(v) => !v && setEditIdent(null)}>
        <DialogContent className="max-w-lg" data-testid="owner-identity-dialog">
          <DialogHeader>
            <DialogTitle>Owner Identity</DialogTitle>
          </DialogHeader>
          {editIdent && (
            <div className="space-y-4">
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                This owner isn't linked to a person record yet — identity is stored on this business only. Linking a person later makes their Address Book record the single source.
              </p>
              <IdentityFields value={editIdent} onChange={setEditIdent} />
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditIdent(null)}>Cancel</Button>
                <Button
                  disabled={saving}
                  onClick={async () => {
                    const { index, ...ident } = editIdent;
                    const next = owners.map((l, i) => (i === index ? {
                      ...l,
                      name: ident.name || null, cell_phone: ident.cell_phone || null,
                      email: ident.email || null, dob: ident.dob || null,
                      home_address: ident.home_address,
                    } : l));
                    setEditIdent(null);
                    await saveOwners(next, 'Owner identity updated');
                  }}
                  data-testid="owner-identity-save"
                >
                  Save
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove confirmation */}
      <AlertDialog open={removeIdx !== null} onOpenChange={(v) => !v && setRemoveIdx(null)}>
        <AlertDialogContent data-testid="owner-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" /> Remove owner from this business?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This removes {removeIdx !== null ? (resolveIdentity(owners[removeIdx] || {}, persons[(owners[removeIdx] || {}).person_id]).name || 'this owner') : ''} from {contact.company_name}'s owners/guarantors.
              {removeIdx !== null && owners[removeIdx]?.person_id
                ? ' Their person record stays in the Address Book.'
                : ' Their inline identity on this business will be removed.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleRemove} data-testid="owner-remove-confirm-btn">
              Remove Owner
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

// ── shared field groups ───────────────────────────────────────────────────────
const RelationshipFields = ({ value, onChange }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label>Title</Label>
        <Input value={value.title || ''} onChange={(e) => onChange({ ...value, title: e.target.value })} placeholder="e.g. President" data-testid="owner-title-input" />
      </div>
      <div>
        <Label>Ownership %</Label>
        <Input type="number" min="0" max="100" step="0.1" value={value.ownership_pct ?? ''} onChange={(e) => onChange({ ...value, ownership_pct: e.target.value })} placeholder="e.g. 75" data-testid="owner-pct-input" />
      </div>
    </div>
    <div className="flex gap-6 pt-1">
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox checked={value.is_owner} onCheckedChange={(v) => onChange({ ...value, is_owner: !!v })} data-testid="owner-is-owner-checkbox" />
        Owner
      </label>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox checked={value.is_guarantor} onCheckedChange={(v) => onChange({ ...value, is_guarantor: !!v })} data-testid="owner-is-guarantor-checkbox" />
        Guarantor
      </label>
    </div>
  </div>
);

const IdentityFields = ({ value, onChange }) => {
  const ha = value.home_address || {};
  const setHa = (k, v) => onChange({ ...value, home_address: { ...ha, [k]: v } });
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2">
        <Label>Full Name</Label>
        <Input value={value.name || ''} onChange={(e) => onChange({ ...value, name: e.target.value })} data-testid="owner-ident-name" />
      </div>
      <div>
        <Label>Cell Phone</Label>
        <Input value={value.cell_phone || ''} onChange={(e) => onChange({ ...value, cell_phone: e.target.value })} placeholder="555-123-4567" data-testid="owner-ident-cell" />
      </div>
      <div>
        <Label>Email</Label>
        <Input type="email" value={value.email || ''} onChange={(e) => onChange({ ...value, email: e.target.value })} data-testid="owner-ident-email" />
      </div>
      <div>
        <Label>Date of Birth</Label>
        <Input type="date" value={value.dob || ''} onChange={(e) => onChange({ ...value, dob: e.target.value })} data-testid="owner-ident-dob" />
      </div>
      <div className="col-span-2 pt-1">
        <p className="text-xs font-medium text-slate-500 border-b pb-1 mb-2">Home Address</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Street</Label><Input value={ha.street1 || ''} onChange={(e) => setHa('street1', e.target.value)} data-testid="owner-ident-street1" /></div>
          <div className="col-span-2"><Label>Street 2</Label><Input value={ha.street2 || ''} onChange={(e) => setHa('street2', e.target.value)} placeholder="Apt / Suite (optional)" /></div>
          <div><Label>City</Label><Input value={ha.city || ''} onChange={(e) => setHa('city', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>State</Label><Input value={ha.state || ''} onChange={(e) => setHa('state', e.target.value)} /></div>
            <div><Label>ZIP</Label><Input value={ha.zip || ''} onChange={(e) => setHa('zip', e.target.value)} maxLength={10} /></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Add Owner: search Address Book people first, create person if needed ──────
const AddOwnerDialog = ({ open, onOpenChange, getAuthHeaders, businessName, excludePersonIds, onAdd }) => {
  const [mode, setMode] = useState('search'); // 'search' | 'create'
  const [query, setQuery] = useState('');
  const [people, setPeople] = useState(null);
  const [selected, setSelected] = useState(null);       // person contact
  const [identity, setIdentity] = useState(emptyIdentity);
  const [rel, setRel] = useState(emptyRelationship);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode('search'); setQuery(''); setSelected(null);
    setIdentity(emptyIdentity); setRel(emptyRelationship);
    axios.get(`${API}/contacts`, { headers: getAuthHeaders() })
      .then((res) => setPeople(res.data.filter((c) => c.is_person)))
      .catch(() => setPeople([]));
  }, [open, getAuthHeaders]);

  const matches = useMemo(() => {
    if (!people) return [];
    const q = query.trim().toLowerCase();
    return people
      .filter((p) => !excludePersonIds.includes(p.id))
      .filter((p) => !q || [p.company_name, p.name, p.email, p.phone, p.shortcode].some((v) => (v || '').toLowerCase().includes(q)))
      .slice(0, 8);
  }, [people, query, excludePersonIds]);

  const createPerson = async () => {
    if (!identity.name?.trim()) { toast.error('Person name is required'); return null; }
    const payload = {
      company_name: identity.name.trim(), name: identity.name.trim(),
      is_person: true, phone: identity.cell_phone || '', email: identity.email || '',
      dob: identity.dob || null, home_address: identity.home_address,
    };
    let code = genShortcode(identity.name);
    for (let attempt = 0; attempt < 4; attempt += 1) {
      try {
        const res = await axios.post(`${API}/contacts`, { ...payload, shortcode: code }, { headers: getAuthHeaders() });
        return res.data;
      } catch (err) {
        if (err.response?.status === 409) { code = `${genShortcode(identity.name)}${Math.floor(10 + Math.random() * 89)}`; continue; }
        const d = err.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : 'Failed to create person');
        return null;
      }
    }
    toast.error('Could not find an available shortcode — create the person from the Address Book page.');
    return null;
  };

  const submit = async () => {
    setBusy(true);
    try {
      let personId = selected?.id || null;
      if (mode === 'create') {
        const person = await createPerson();
        if (!person) return;
        personId = person.id;
        toast.success(`Person "${person.company_name}" added to the Address Book`);
      }
      const link = {
        person_id: personId,
        title: rel.title || null,
        ownership_pct: rel.ownership_pct === '' || rel.ownership_pct === null ? null : parseFloat(rel.ownership_pct),
        is_owner: !!rel.is_owner, is_guarantor: !!rel.is_guarantor,
        name: null, cell_phone: null, email: null, dob: null,
        home_address: { street1: null, street2: null, city: null, state: null, zip: null },
      };
      await onAdd(link);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="add-owner-dialog">
        <DialogHeader>
          <DialogTitle>Add Owner / Guarantor to {businessName}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2">
          <Button variant={mode === 'search' ? 'default' : 'outline'} size="sm" onClick={() => setMode('search')} data-testid="add-owner-mode-search">
            <Search className="h-3 w-3 mr-1" /> Existing person
          </Button>
          <Button variant={mode === 'create' ? 'default' : 'outline'} size="sm" onClick={() => { setMode('create'); setSelected(null); }} data-testid="add-owner-mode-create">
            <Plus className="h-3 w-3 mr-1" /> New person
          </Button>
        </div>

        {mode === 'search' && (
          <div className="space-y-2">
            <Input placeholder="Search people by name, email, phone…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="add-owner-search-input" autoFocus />
            <div className="max-h-44 overflow-y-auto border rounded-md divide-y">
              {people === null ? (
                <p className="text-sm text-gray-400 p-3">Loading people…</p>
              ) : matches.length === 0 ? (
                <div className="p-3 text-sm text-gray-500">
                  No matching people in the Address Book.
                  <button className="text-blue-600 hover:underline ml-1" onClick={() => setMode('create')}>Create one instead?</button>
                </div>
              ) : matches.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelected(p)}
                  className={`w-full text-left px-3 py-2 hover:bg-blue-50 ${selected?.id === p.id ? 'bg-blue-50 border-l-2 border-blue-600' : ''}`}
                  data-testid={`add-owner-person-${p.shortcode}`}
                >
                  <p className="text-sm font-medium">{p.company_name} <span className="font-mono text-xs text-blue-600">({p.shortcode})</span></p>
                  <p className="text-xs text-gray-500">{[p.phone, p.email, p.dob ? `DOB ${p.dob}` : null].filter(Boolean).join(' · ') || 'No contact info yet'}</p>
                </button>
              ))}
            </div>
            {selected && (
              <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
                Linking <strong>{selected.company_name}</strong> — their identity (cell, email, DOB, home address) stays on their person record and is shared across all their businesses.
              </p>
            )}
          </div>
        )}

        {mode === 'create' && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">Creates a Person record in the Address Book, then links it to this business — so they never have to be re-entered for their next company.</p>
            <IdentityFields value={identity} onChange={setIdentity} />
          </div>
        )}

        <div className="pt-2 border-t">
          <p className="text-xs font-medium text-slate-500 mb-2">Relationship to {businessName}</p>
          <RelationshipFields value={rel} onChange={setRel} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || (mode === 'search' && !selected)} data-testid="add-owner-submit">
            {busy ? 'Saving…' : mode === 'create' ? 'Create Person & Link' : 'Link Owner'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OwnersSection;
